import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject, ReactNode } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import styles from './HorizontalCreativity.module.scss';
import { setupSlidesTimeline, setupTitleTimeline } from './HorizontalCreativity.transitions';
import type { HorizontalCreativityProps } from './HorizontalCreativity.data';
import Heading from '../../atoms/heading/Heading';
import useScrollTrigger from '../../../hooks/useScrollTrigger';
import useSetupTimeline from '../../../hooks/useSetupTimeline';
import useWindowSize from '../../../hooks/useWindowSize';
import gsap from 'gsap';
import Image from '../../atoms/image/Image';
import { cleanMediaQueries } from '../../../hooks/useDeviceStateTracker';
import type { ImageRef } from '../../atoms/image/Image.data';
import { useMobileViewport } from '../../../hooks/useMobileViewport';
import sharedVariables from '../../../data/shared-variable/shared-variables.json';
import { NODE_PARAGRAPH, render } from 'storyblok-rich-text-react-renderer';
import Text from '../../atoms/text/Text';
import { isTouch } from '../../../hooks/useIsDesktopDevice';
import type { TextRef } from '../../atoms/text/Text.data';

const originalShapeSize = {
  w: 799,
};

export default function HorizontalCreativity({
  blok,
  ref,
  ...props
}: HorizontalCreativityProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const contentFrameRef = useRef<HTMLDivElement>(null);
  const title1Ref = useRef<HTMLHeadingElement>(null);
  const title2Ref = useRef<HTMLHeadingElement>(null);
  const titleOutline1Ref = useRef<HTMLHeadingElement>(null);
  const titleOutline2Ref = useRef<HTMLHeadingElement>(null);
  const shapePathRef = useRef<SVGPathElement>(null);
  const shapeRef = useRef<HTMLDivElement>(null);
  const slidesRef = useRef<Array<HTMLDivElement>>([]);
  const slideImagesRef = useRef<Array<ImageRef>>([]);
  const pinWrapperRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<TextRef>(null);

  const imageRef = useRef<ImageRef>(null);
  const hoverTimeline = useRef<gsap.core.Timeline>(undefined);
  const slidesTimeline = useRef<gsap.core.Timeline>(undefined);
  const shapeInnerRef = useRef<HTMLDivElement>(null);
  const imageHolderRef = useRef<HTMLDivElement>(null);
  const isMobileViewport = useMobileViewport(sharedVariables.deviceState.MIN_768, false);

  hoverTimeline.current = useSetupTimeline(setupTitleTimeline, {
    title1Ref,
    title2Ref,
    titleOutline1Ref,
    titleOutline2Ref,
    shapeRef,
  });

  slidesTimeline.current = useSetupTimeline(setupSlidesTimeline, {
    slidesRef,
    slideImagesRef,
    shapeInnerRef,
    imageRef,
  });

  const { width, height } = useWindowSize();

  const getSize = useCallback(() => {
    const shapeBounds = shapeRef?.current?.getBoundingClientRect();
    return (shapeBounds?.width || 0) / originalShapeSize.w;
  }, [shapeRef]);

  const setSize = useCallback(
    (scale: number) => {
      gsap.set(shapePathRef.current, {
        attr: { transform: `scale(${scale})` },
      });
    },
    [shapePathRef],
  );

  useEffect(() => {
    setSize(getSize());
  }, [width, height, setSize, getSize]);

  const currentSlideIndex = useRef<number | undefined>(undefined);
  const endTrigger = isMobileViewport ? '0%' : '300%';

  useScrollTrigger({
    trigger: elementRef,
    endTrigger: pinWrapperRef,
    animation: hoverTimeline.current,
    vars: {
      scrub: true,
      start: 'top bottom',
      end: `bottom top-=${endTrigger}`,
      anticipatePin: 1,
      onUpdate: ({ progress }) => {
        let index;

        if (progress > 0) {
          index = -1;
        }

        if (progress > 0.1) {
          index = 0;
        }
        if (progress > 0.25) {
          index = 1;
        }
        if (progress > 0.4) {
          index = 2;
        }
        if (progress > 0.55) {
          index = 3;
        }
        if (progress > 0.7) {
          index = 4;
        }

        if (progress > 0.9) {
          index = 5;
        }

        if (typeof index === 'number' && currentSlideIndex.current !== index) {
          slidesTimeline?.current?.tweenTo(`slide_${index}`);
        }

        currentSlideIndex.current = index;
      },
    },
  });

  useScrollTrigger({
    trigger: pinWrapperRef,
    pin: contentFrameRef,
    disabled: isMobileViewport,
    vars: {
      scrub: true,
      start: 'center center',
      end: `+=${endTrigger}`,
      pinType: 'transform',
      anticipatePin: 1,
    },
  });

  useScrollTrigger({
    trigger: descriptionRef,
    transition: {
      ref: descriptionRef,
    },
  });

  const clipId = 'drop';

  return (
    <div
      ref={elementRef}
      className={classNames(styles.horizontalCreativity, props.className)}
      {...sbEditable(blok)}
    >
      <svg
        preserveAspectRatio="xMinYMin meet"
        viewBox="0 0 799 799"
        className={classNames(styles.svg)}
      >
        <defs>
          <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
            <path
              fill="white"
              ref={shapePathRef}
              d="M399.167 0C178.715 0 0 178.715 0 399.167C0 619.618 178.715 798.333 399.167 798.333C619.618 798.333 798.333 619.618 798.333 399.167V0H399.167Z"
            />
          </clipPath>
        </defs>
      </svg>

      <div ref={pinWrapperRef} className={classNames(styles.pinWrapper)}>
        <div ref={contentFrameRef} className={classNames(styles.contentFrame, 'grid-gutter')}>
          <div className={classNames(styles.container)}>
            <div className={classNames(styles.titleWrapper)}>
              <Heading
                ref={title1Ref}
                as="h2"
                size="heading-01"
                className={classNames(styles.title, styles.line1)}
                disableTriggerInOnScroll
                transitionInType="none"
              >
                {blok.line_1}
              </Heading>
              <Heading
                ref={title2Ref}
                as="h2"
                size="heading-01"
                className={classNames(styles.title, styles.line2)}
                disableTriggerInOnScroll
                transitionInType="none"
              >
                {blok.line_2}
              </Heading>
            </div>
            <div
              ref={shapeRef}
              style={{
                clipPath: `url(#${clipId})`,

                WebkitClipPath: `url(#${clipId})`,
              }}
              className={classNames(styles.shape)}
            >
              <div ref={shapeInnerRef} className={classNames(styles.shapeInner)}>
                <div ref={imageHolderRef} className={classNames(styles.imageHolder)}>
                  <Image
                    ref={imageRef}
                    src={blok.image_1}
                    srcDesktop={blok.image_1}
                    className={classNames(styles.image, 'abs-fill')}
                    layout="fill"
                    objectFit="cover"
                    alt={`${blok.line_1} ${blok.line_2}`}
                    sizes={`${cleanMediaQueries.MIN_768} 828px, ${cleanMediaQueries.MIN_1920} 828px, 100vw`}
                  />

                  {[blok.image_2, blok.image_3, blok.image_4, blok.image_5].map((image, index) => (
                    <div
                      ref={(ref) => {
                        slidesRef.current[index] = ref as ImageRef;
                      }}
                      key={image}
                      style={{
                        clipPath: `url(#${clipId})`,

                        WebkitClipPath: `url(#${clipId})`,
                      }}
                      className={classNames(styles.imageContainer, 'abs-fill')}
                    >
                      <Image
                        ref={(ref) => {
                          slideImagesRef.current[index] = ref as ImageRef;
                        }}
                        src={image}
                        srcDesktop={image}
                        className={classNames(styles.image, 'abs-fill')}
                        layout="fill"
                        objectFit="cover"
                        sizes={`${cleanMediaQueries.MIN_768} 828px, ${cleanMediaQueries.MIN_1920} 828px, 100vw`}
                        alt={`${blok.line_1} ${blok.line_2}`}
                      />
                    </div>
                  ))}
                </div>

                <div className={classNames(styles.titleWrapper)}>
                  <Heading
                    ref={titleOutline1Ref}
                    as="h2"
                    size="heading-01"
                    className={classNames(styles.title, styles.line1, 'text-outline')}
                    disableTriggerInOnScroll
                    transitionInType="none"
                  >
                    {blok.line_1}
                  </Heading>
                  <Heading
                    ref={titleOutline2Ref}
                    as="h2"
                    size="heading-01"
                    className={classNames(styles.title, styles.line2, 'text-outline')}
                    disableTriggerInOnScroll
                    transitionInType="none"
                  >
                    {blok.line_2}
                  </Heading>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={classNames('grid-gutter max-content-width')}>
        {render(blok.description, {
          nodeResolvers: {
            [NODE_PARAGRAPH]: (children: ReactNode) => (
              <>
                <Text
                  ref={descriptionRef}
                  transitionInType={isTouch ? 'none' : 'splitLineRotateIn'}
                  className={classNames(styles.description)}
                  as="p"
                  size="quote"
                  disableTriggerInOnScroll
                >
                  {children}
                  <br />
                </Text>
              </>
            ),
          },
        })}
      </div>
    </div>
  );
}
